import { useContext, useEffect } from "react"
import { Dialog, Button, Typography, Stack, IconButton, Divider, FormGroup, FormControlLabel, Checkbox, Box, TextField } from "@mui/material"
import { ToolContext } from "../../../../../core/context/ToolContext"
import { Close, AddCircleOutline } from "@mui/icons-material"
import { rqx } from "../../../../../core/request/API"
import { env } from "../../../../../core/Env"
import { SnakeLoader } from "../../../../../core/loader/SnakeLoader"
import { fieldStyle } from "../../../../../core/global/realproperty/Index"
import { SuccessMsg } from "../../../../../core/global/SuccessMsg"

export const RequirementModal = ({ reload }) => {
    const { realproperty_state, tool_state } = useContext(ToolContext)
    const { requirements, newReqName, inquiryData, fileTypes } = realproperty_state
    const classes = fieldStyle();

    const handleClose = () => requirements.set((p) => ({ ...p, modal: false }))
    
    const __init = async () => {
        try {
            requirements.set((p) => ({ ...p, loader: true }))
            const data = await rqx.get(`<<tool>>/${env()}/propertyListing/read_requirements`, { company_id: tool_state.__SESSION.data.uaccess[0].company_id, ref: requirements.data.modalType });
            const existingReqs = requirements.data.modalType === 'seller' ? fileTypes.data.sellerAvailReq.map(v => v.id) : fileTypes.data.buyerAvailReq.map(v => v.id);
            const matchReqs = data.filter(v => existingReqs.includes(v.id))
            requirements.set((p) => ({ 
                ...p, 
                avail_reqs: data,
                loader: false,
                match_reqs: matchReqs,
            }))
        } catch (error) {
            console.error(error);
        }
    }

    const handleCheckboxChange = (e, id) => {
        requirements.set((p) => {
            const existing = p.match_reqs || []
            const isIncluded = existing.some(v => v.id === id)
            const target = requirements.data.avail_reqs.find(v => v.id === id)
            
            return {
                ...p,
                error: false,
                match_reqs: e.target.checked
                    ? isIncluded ? existing : [...existing, target]
                    : existing.filter(v => v.id !== id)
            }         
        })  
    }

    const handleNewReqName = (e) => {
        newReqName.set((p) => ({ 
            ...p,
            name: {
                ...p.name, 
                value: e.target.value,
                stat: e.target.value.trim() === '',
                exist: false,
            }
        }))
    }
    
    const handleSubmitNewReq = async () => {
        if (newReqName.data.name.value.trim() === '') {
            newReqName.set((p) => ({ 
                ...p,
                name: {
                    ...p.name, 
                    stat: newReqName.data.name.value.trim() === ''
                }
            }))
            return
        }
        try {
            newReqName.set((p) => ({ ...p, loader: true }))
            const body = {
                requirement_name: newReqName.data.name.value,
                type: requirements.data.modalType === 'seller' ? 1 : 2, // buyer = 2
                company_id: tool_state.__SESSION.data.uaccess[0].company_id,
                is_default: 0,
            }
            const u = await rqx.post(`<<tool>>/${env()}/propertyListing/create_property_requirement`, body)
            if (u.message === 'success') {
                newReqName.set((p) => ({ ...p, loader: false, success: true, addReqModal: false, name: { ...p.name, value: '', stat: false, msg: 'Name is required!' } }))
                __init();
            } else if (u.message === 'exist') {
                newReqName.set((p) => ({ ...p, loader: false, name: { ...p.name, exist: true, exist_msg: 'Requirement already exist!' } }))
            }
        } catch (error) {
            console.error(error);
        }
    }

    const handleSubmitReq = async () => {
        try {
            requirements.set((p) => ({ ...p, sendLoader: true }))
            const body = {
                company_id: tool_state.__SESSION.data.uaccess[0].company_id,
                listing_id: inquiryData.data?.data[0].listing_id,
                user_id: tool_state.userId.data,
                property_requirement_ids: requirements.data.match_reqs.map(v => v.id),
                type: requirements.data.modalType === 'seller' ? 1 : 2,
            }
            const u = await rqx.post(`<<tool>>/${env()}/propertyListing/create_company_property_requirement`, body)
            if (u.message === 'success') {
                requirements.set((p) => ({ ...p, sendLoader: false, sendSuccess: true, modal: false }))
                reload();
            }
        } catch (error) {
            console.error(error);
        }
    }
        
    const handleOpenReqModal = () => newReqName.set((p) => ({ ...p, addReqModal: true }))

    const handleCloseReqModal = () => {
        newReqName.set((p) => ({ ...p, addReqModal: false, name: { value: '', stat: false, msg: 'Name is required!', exist: false, exist_msg: 'Requirement already exist!' } }))
    }

    useEffect(() => (
        __init()
        // eslint-disable-next-line
    ), [])

    return (
        <Dialog open={requirements?.data?.modal || false} onClose={handleClose} PaperProps={{ sx: { borderRadius: '1rem', p: '1.5rem' } }}>
            { requirements.data.loader ?
                <Box display="flex" justifyContent="center"><SnakeLoader size="10px" bg="#11783C" distance="6px"/></Box>
            :
                <Stack gap='0.5rem'>
                    <Stack gap='0.5rem'>
                        <Stack direction='row' alignItems='center' justifyContent='space-between'>
                            <Typography fontSize={18} fontWeight={600}>{requirements.data.modalType === 'seller' ? 'Seller' : 'Buyer'} Requirements</Typography>
                            <IconButton onClick={handleClose} sx={{ p: 0 }}>
                                <Close sx={{ color: 'gray', '&:hover' : { color: 'error.main' } }}/>
                            </IconButton>
                        </Stack>
                        <Typography fontSize={12} sx={{ color: 'text.secondary' }}>Select the required documents the {requirements.data.modalType} must provide for this property.</Typography>
                    </Stack>
                    <Divider/>
                    <Stack gap='0.5rem'>
                        <FormGroup>
                            {requirements.data.avail_reqs?.map((v, k) => (
                                <FormControlLabel key={k} label={v.name} control={ <Checkbox size="small" checked={requirements.data.match_reqs.some(r => r.id === v.id)} onChange={(e) => handleCheckboxChange(e, v.id)}/>} />
                            ))}
                        </FormGroup>
                        <Stack onClick={handleOpenReqModal} direction='row' alignItems='center' gap='0.5rem' p='0.5rem' justifyContent='center' sx={{ border: '1px solid #11783C', borderRadius: '20px', cursor: 'pointer', transition: '0.25s', '&:hover': {  bgcolor: 'rgba(0, 0, 0, 0.04)', boxShadow: '0px 1px 4px rgba(0,0,0,0.3)' } }}>
                            <IconButton sx={{ p: 0 }}>
                                <AddCircleOutline sx={{ color: 'primary.main' }}/>
                            </IconButton>
                            <Typography fontSize={14} fontWeight={600} sx={{ color: 'text.main' }}>Add a custom file requirement for the {requirements.data.modalType}</Typography>
                        </Stack>
                    </Stack>
                    <Divider sx={{ my: '1rem' }}/>
                    { requirements.data.sendLoader ?
                        <Box display="flex" justifyContent="center"><SnakeLoader size="10px" bg="#11783C" distance="6px" /></Box>
                    :
                        <Stack alignItems='center'>
                            <Button onClick={handleSubmitReq} variant='contained' sx={{ width: '5rem' }} disabled={requirements.data.match_reqs.length === 0}>Submit</Button>
                        </Stack>
                    }
                </Stack>
            }
            { newReqName.data.addReqModal &&
                <Dialog open={newReqName.data.addReqModal} onClose={handleCloseReqModal} PaperProps={{ sx: { borderRadius: '1rem', p: '1.5rem' } }}>
                    <Stack gap='1.5rem'>
                        <Stack gap='0.5rem'>
                            <Typography fontSize={14}>File Requirement Name:</Typography>
                            <TextField size="small" className={classes.tf} value={newReqName.data.name.value} onChange={handleNewReqName} error={newReqName.data.name.stat || newReqName.data.name.exist} helperText={newReqName.data.name.exist ? newReqName.data.name.exist_msg : ''} FormHelperTextProps={{
                                style: {
                                    backgroundColor: 'transparent',
                                    padding: '0px',
                                }
                            }}/>
                        </Stack>
                        { newReqName.data.loader ?
                            <Box display="flex" justifyContent="center"><SnakeLoader size="10px" bg="#11783C" distance="6px" /></Box>
                        :
                            <Stack direction='row' alignItems='center' justifyContent='space-around'>
                                <Button onClick={handleCloseReqModal} variant='outlined' size='small' color='error'>Cancel</Button>
                                <Button onClick={handleSubmitNewReq} variant='contained' size='small'>Submit</Button>
                            </Stack>
                        }
                    </Stack>
                </Dialog>
            }
            { newReqName.data.success && <SuccessMsg open={newReqName.data.success} setOpen={(v) => newReqName.set(p => ({ ...p, success: v }))} message="Success" bgcolor="#d2f9e2" hideDuration={null}/> }
        </Dialog>
    )
}